import Tree from "rc-tree";
import React, {FC, useState, useEffect} from "react";
import "./styles.css";
import {DataNode, Key} from "rc-tree/lib/interface";

// Add custom styles to ensure full row highlighting
const customStyles = `
.highlighted-node .rc-tree-node-content-wrapper {
    background-color: yellow !important;
    display: block;
    width: 100%;
}
`;

import {TreeExportOptions} from "../../../models";
import {
    exportCompleteTreeToExcelUsingExcelJs,
    exportTreeWithOptionsUsingExcelJs,
    exportTreeWithOptionsUsingXlsx,
} from "../../../utils";

export const TreeView: FC<{
    onSelect?: (key: Key[]) => void;
    treeData: DataNode[];
    loading?: boolean;
    selectedKeys: Key[];
    resetTrigger?: number;
}> = ({ treeData, loading, onSelect, selectedKeys, resetTrigger = 0 }) => {
    //#region state
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
    const [highlightedKeys, setHighlightedKeys] = useState<Key[]>([]);
    const [lastResetTrigger, setLastResetTrigger] = useState<number>(resetTrigger);
    //#endregion

    //#region functions
    const toggleExpand = (key: Key) => {
        if (expandedKeys.includes(key)) {
            // If key is already expanded, remove it from the expandedKeys
            setExpandedKeys(expandedKeys.filter(expKey => expKey !== key));
        } else {
            // Otherwise add it to expandedKeys
            setExpandedKeys([...expandedKeys, key]);
        }
    };

    const expandToSelectedNode = (nodes: DataNode[], selectedKey: Key) => {
        for (const node of nodes) {
            if (node.key === selectedKey) {
                return true;// Found the selected node, stop further recursion
            }
            if (node.children) {
                if (expandToSelectedNode(node.children, selectedKey)) {
                    if (!expandedKeys.includes(node.key)) {
                        setExpandedKeys((prevExpandedKeys) => [
                            ...prevExpandedKeys,
                            node.key,
                        ]);
                    }
                    return true; // Stop further recursion
                }
            }
        }
        return false;
    };

    const exportToExcel = (options: TreeExportOptions = {}) => {
        exportTreeWithOptionsUsingXlsx(
            treeData,
            expandedKeys,
            selectedKeys,
            options
        );
    };

    const handleExport = () => {
        exportToExcel({
            fileName: 'tree_data_export.xlsx',
            exportFormat: 'flat'
        });
    };

    const handleFormattedExport = () => {
        exportToExcel({
            fileName: 'account_tree_visual_export.xlsx',
            exportFormat: 'indented'
        });
    };

    const handleExportCurrentView = async () => {
        try {
            setIsExporting(true);
            await exportTreeWithOptionsUsingExcelJs(
                treeData,
                expandedKeys,
                selectedKeys,
                {
                    fileName: 'tree_current_view.xlsx',
                    exportFormat: 'flat'
                }
            );
        } catch (error) {
            console.error('Error exporting tree:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportAsTree = async () => {
        try {
            setIsExporting(true);
            await exportTreeWithOptionsUsingExcelJs(
                treeData,
                expandedKeys,
                selectedKeys,
                {
                    fileName: 'tree_visual.xlsx',
                    exportFormat: 'indented'
                }
            );
        } catch (error) {
            console.error('Error exporting tree:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportCompleteTree = async () => {
        try {
            setIsExporting(true);
            await exportCompleteTreeToExcelUsingExcelJs(
                treeData,
                selectedKeys,
                'complete_tree.xlsx'
            );
        } catch (error) {
            console.error('Error exporting tree:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleNodeClick = (selectedKeys: Key[], info: any) => {
        const nodeKey = info.node.key;

        // If selectedKeys is empty, it means we're deselecting the node
        if (selectedKeys.length === 0) {
            setHighlightedKeys([]); // Clear all highlights when deselecting
        } else {
            setHighlightedKeys(selectedKeys); // Only highlight the currently selected nodes
        }

        // Call onSelect with the selected keys if provided
        if (onSelect) {
            onSelect(selectedKeys);
        }

        // Toggle expand state for the clicked node
        toggleExpand(nodeKey);
    };

    const processTreeData = (nodes: DataNode[]): DataNode[] => {
        return nodes.map(node => {
            const processed: DataNode = {
                ...node,
                className: highlightedKeys.includes(node.key) ? 'highlighted-node' : ''
            };

            if (node.children) {
                processed.children = processTreeData(node.children);
            }

            return processed;
        });
    };
    //#endregion

    //#region variables
    const processedTreeData = processTreeData(treeData);
    //#endregion

    //#region useEffect
    useEffect(() => {
        if (selectedKeys.length > 0) {
            expandToSelectedNode(treeData, selectedKeys[0]);

            // Update highlighted keys based on selection
            setHighlightedKeys(selectedKeys);
        } else {
            // Clear highlights when there are no selected keys
            setHighlightedKeys([]);
        }
    }, [selectedKeys, treeData]);

    useEffect(() => {
        // Only run if resetTrigger has changed from its last value
        if (resetTrigger !== lastResetTrigger) {
            // Clear highlighted keys
            setHighlightedKeys([]);

            // Clear expanded keys
            setExpandedKeys([]);

            // Update the last trigger value
            setLastResetTrigger(resetTrigger);

            // Clear selection via callback
            if (onSelect) {
                onSelect([]);
            }
        }
    }, [resetTrigger, onSelect]);
    //#endregion

    //#region html
    return (
        <>
            {/* Add custom styles for highlighting */}
            <style>{customStyles}</style>

            {/*<ButtonBox size="sm"  className="mx-3"  iconType="download"  disabled={loading || isExporting || treeData.length === 0}  onClick={handleFormattedExport}>{getLabelName("Export")}</ButtonBox>*/}
            {/*<ButtonBox size="sm"  className="mx-3"  iconType="download"  disabled={loading || isExporting || treeData.length === 0}  onClick={handleExportCurrentView}>{getLabelName("Export")}</ButtonBox>*/}
            {/*current button*/}
            {/*<ButtonBox size="sm"  className="mx-3"  iconType="download" disabled={loading || isExporting || treeData.length === 0} onClick={handleExportAsTree}>{getLabelName("Export")}</ButtonBox>*/}
            {/*<ButtonBox size="sm"  className="mx-3"  iconType="download"  disabled={loading || isExporting || treeData.length === 0}  onClick={handleExportCompleteTree}>{getLabelName("Export")}</ButtonBox>*/}
            {treeData.length !== 0 && (
                <>
                    <Tree
                        showLine
                        showIcon={false}
                        onSelect={handleNodeClick}
                        treeData={processedTreeData}
                        checkable={false}
                        selectable={true}
                        selectedKeys={selectedKeys}
                        expandedKeys={expandedKeys}
                        onExpand={(keys) => {
                            setExpandedKeys(keys);
                        }}
                    />
                </>
            )}
        </>
    );
    //#endregion
};